import { FC, ReactNode } from 'react';

import { Permission } from './permissions';
import { useBamAuth } from './useBamAuth';

type PermissionGateProps = {
  /** Single permission required */
  permission?: Permission;
  /** Renders if user has ANY of these permissions */
  anyOf?: Permission[];
  /** Renders if user has ALL of these permissions */
  allOf?: Permission[];
  /** Optional fallback when permission is denied */
  fallback?: ReactNode;
  children: ReactNode;
};

/**
 * Declarative permission gate component.
 *
 * Usage:
 *   <PermissionGate permission="agenda.plan"><Button>Create</Button></PermissionGate>
 *   <PermissionGate anyOf={['agenda.plan', 'agenda.mutate']}><EditPanel /></PermissionGate>
 *   <PermissionGate allOf={['ddp.view', 'ddp.mutate']} fallback={<ReadOnlyView />}><EditView /></PermissionGate>
 */
const PermissionGate: FC<PermissionGateProps> = ({ permission, anyOf, allOf, fallback = null, children }) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = useBamAuth();

  let allowed = false;

  if (permission) {
    allowed = hasPermission(permission);
  } else if (anyOf) {
    allowed = hasAnyPermission(anyOf);
  } else if (allOf) {
    allowed = hasAllPermissions(allOf);
  }

  return allowed ? <>{children}</> : <>{fallback}</>;
};

export default PermissionGate;

/**
 * Hook variant for non-JSX permission checks.
 *
 * Usage:
 *   const canEdit = useHasPermission('agenda.mutate');
 *   if (canEdit) { ... }
 */
export function useHasPermission(permission: Permission): boolean {
  const { hasPermission } = useBamAuth();
  return hasPermission(permission);
}
